import React, {useCallback, useMemo, useRef, useState} from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import type {EmailList} from "../../../../types/emailList";
import {useReplaceContactsInListMutation} from "../../../../services/emailListApi";
import {toast} from "react-toastify";

interface ReplaceContactsDialogProps {
  open: boolean;
  onClose: () => void;
  emailList: EmailList | null;
}

export default function ReplaceContactsDialog({open, onClose, emailList}: ReplaceContactsDialogProps) {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [replaceContactsApi, {isLoading}] = useReplaceContactsInListMutation();

  const borderColor = dragActive ? theme.palette.primary.main : theme.palette.divider;
  const bg = dragActive
    ? (theme.palette.mode === "light" ? "#F6FAFF" : "rgba(33,150,243,0.08)")
    : "transparent";

  const handleBrowse = () => inputRef.current?.click();

  const handleFiles = useCallback((filesLike: FileList | File[]) => {
    const files = Array.from(filesLike);
    setPicked(files);
  }, []);

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) {
      handleFiles(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const pickedLabel = useMemo(() => {
    if (!picked.length) return "";
    if (picked.length === 1) return picked[0].name;
    return `${picked.length} files selected`;
  }, [picked]);

  const canReplace = picked.length > 0 && !isLoading;

  const handleReplace = async () => {
    if (!emailList || !picked[0]) return;
    try {
      await replaceContactsApi({
        emailListId: emailList.id,
        file: picked[0],
      }).unwrap();
      toast.success("Contacts replaced successfully.");
    } catch (e: any) {
      toast.error("Failed to replace contacts", e.data?.message || e.message || e.toString());
      console.error("Error replacing contacts:", e);
    }
    setPicked([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Replace contacts</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">1) Upload your file</Typography>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Select a file to replace all existing contacts.
          </Typography>

          <Box
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={handleBrowse}
            role="button"
            aria-label="Upload area"
            tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleBrowse()}
            sx={{
              border: `2px dashed ${borderColor}`,
              borderRadius: 2,
              minHeight: 220,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: bg,
              transition: "all .15s ease",
              cursor: "pointer",
              px: 2
            }}
          >
            <Stack spacing={1} alignItems="center" textAlign="center">
              <CloudUploadIcon sx={{fontSize: 56, opacity: 0.7}} />
              <Typography variant="subtitle1">
                Select your file or drag and drop it here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                .csv, .xlsx or .txt
              </Typography>
              {pickedLabel && (
                <Typography variant="body2" sx={{mt: 1}}>
                  Chosen: <strong>{pickedLabel}</strong>
                </Typography>
              )}
              <Button
                variant="outlined"
                sx={{mt: 1}}
                onClick={(e) => {
                  e.stopPropagation();
                  handleBrowse();
                }}
                disabled={isLoading}
              >
                Browse files
              </Button>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.txt"
                hidden
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{justifyContent: "unset"}}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{width: "100%"}}>
          <Box sx={{flexGrow: 1}} />
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button
            onClick={handleReplace}
            variant="contained"
            disabled={!canReplace}
          >
            Replace
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
